import React, { memo, useEffect, useState } from 'react'
import { PhoneInput } from 'transfix-ui/components/PhoneInput'
import { DriverInlineDisplay } from 'apps/fleet-planner/components/atoms/DriverInlineDisplay'
import { useDriverApi } from 'apps/fleet-planner/hooks'
import { DriverSingle } from 'apps/fleet-planner/pages/loads/DriverSingle'
import { IInlineEditProps } from 'apps/fleet-planner/types'
import * as Types from 'apps/fleet-planner/types'
import { Button, Card, InlineEditContainer } from 'transfix-ui'

interface IDriverSingleInlineEditProps extends IInlineEditProps<Types.IDriver> {
  fieldIndex: number
  selectedDrivers: Types.ILoad['drivers']
  isExternalLoad?: boolean
}

const CELL_PHONE_REQUIRED_MESSAGE = 'A cell phone number is required for Transfix loads.'

export const DriverSingleInlineEdit = memo<IDriverSingleInlineEditProps>(
  ({
    value: valueProp,
    currentEditField,
    isExternal,
    isExternalLoad,
    onEditModeRequested: onEditModeRequestedProp,
    onChange: onChangeProp = () => {},
    fieldIndex,
    selectedDrivers: selectedDriversProp = [],
    dataTest,
  }) => {
    const [validationMessage, setValidationMessage] = useState<string | undefined>('')
    const [localChanges, setLocalChanges] = useState<Types.IDriver | null | undefined>(valueProp)
    const [cellPhone, setCellPhone] = useState<string>('')
    // a cell phone is required for drivers on External Loads
    const cellPhoneRequired = isExternalLoad && !!localChanges && !localChanges?.cellPhone

    const { error: driverApiError, resetError: resetDriverError, updateDriver } = useDriverApi()

    // Update state when receiving new props
    useEffect(() => {
      setLocalChanges(valueProp)
    }, [valueProp])

    const resetLocalChanges = () => {
      setLocalChanges(valueProp)
    }

    const changeHandler = (change: Types.IDriver | null | undefined) => {
      setLocalChanges(change)
    }

    const saveHandler = (changes: Types.IDriver | null | undefined) => {
      onChangeProp(changes)
      onEditModeRequestedProp('')
      setCellPhone('')
      resetDriverError()
    }

    const onSave = async () => {
      // if we are updating a driver's cellphone, we must first
      // update the driver object before updating the load
      if (cellPhoneRequired && !!localChanges) {
        const changes = {
          ...localChanges,
          cellPhone,
        }
        if (changes.id) {
          const updatedDriver = await updateDriver(changes.id, changes)
          if (updatedDriver) saveHandler(updatedDriver)
        }
      } else {
        saveHandler(localChanges)
      }
    }

    const onCancel = () => {
      resetLocalChanges()
      onEditModeRequestedProp('')
    }

    const NAME = `driver-${fieldIndex}`
    const isEditMode = currentEditField === NAME

    const editModeRequestedHandler = (name?: string) => {
      if (!name) {
        resetLocalChanges()
      }
      onEditModeRequestedProp(name)
    }

    const changeCellPhoneHandler = (event: React.ChangeEvent<HTMLInputElement>) => {
      const { value } = event.target
      setCellPhone(value)
    }

    /**
     * Icons should match "Edit" icons, which are pushed to bottom of div
     * but this only happens for 2nd+ driver
     */
    const editIconLocation = fieldIndex > 0 ? 'bottom' : 'center'

    return (
      <InlineEditContainer
        dataTest={dataTest}
        className='relative mt4'
        name={NAME}
        editModeEnabled={isEditMode}
        editIconLocation={editIconLocation}
        editMode={
          <Card className='pa4'>
            <DriverSingle
              onChange={changeHandler}
              value={localChanges}
              onAddDriver={() => {}}
              onValidationChange={setValidationMessage}
              selectedDrivers={selectedDriversProp}
            />
            {cellPhoneRequired && (
              <PhoneInput
                className='pt2 fs-mask'
                label='Cell Phone'
                placeholder='(000) 000 - 0000'
                value={cellPhone}
                large
                required
                onChange={changeCellPhoneHandler}
                invalid={!!driverApiError || !cellPhone}
                validationMessage={driverApiError || CELL_PHONE_REQUIRED_MESSAGE}
              />
            )}
            <div className='tr'>
              <div className='mt4'>
                <Button
                  color='tertiary'
                  size='large'
                  onClick={onCancel}
                  data-test='inline-edit-cancel-driver'>
                  Cancel
                </Button>
                <Button
                  color='primary'
                  size='large'
                  onClick={onSave}
                  disabled={!!validationMessage || (cellPhoneRequired && !cellPhone)}
                  data-test='inline-edit-save-driver'>
                  Save
                </Button>
              </div>
            </div>
          </Card>
        }
        onEditModeRequested={editModeRequestedHandler}>
        <DriverInlineDisplay
          value={valueProp}
          label={`Driver${selectedDriversProp.length ? ` ${fieldIndex + 1}` : ''}`}
          isExternalDriver={isExternal}
        />
      </InlineEditContainer>
    )
  }
)
